import React, { useState, useEffect, useContext, useMemo } from 'react'
import {
  useTable,
  usePagination,
  useSortBy,
  useFilters,
  useRowSelect,
} from 'react-table'
import { Input, Select } from 'antd'

import { BuilderContext } from '../Sandbox/components/store/store.Builder'
import { ViewFormContext } from '../ViewForm/components/store/store.ViewForm'
import { collectFormAnswersData } from '../../services/api'
import styles from './AnswersV2.module.css'

const { Option } = Select

function AnswersV2() {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageCount, setPageCount] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [headers, setHeaders] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [mySortBy, setMySortBy] = useState(null)

  const [hiddenColumns, setHiddenColumns] = useState([])
  const [selectedRows, setSelectedRows] = useState(new Set())

  const [searchQuery, setSearchQuery] = useState('')

  const [columnSearch, setColumnSearch] = useState({})

  const builderContext = useContext(BuilderContext)
  const viewFormContext = useContext(ViewFormContext)

  function highlightText(text, highlight) {
    if (!highlight.trim()) {
      return text
    }
    const regex = new RegExp(`(${highlight})`, 'gi')
    return text.replace(regex, `<span class="${styles.highlight}">$1</span>`)
  }

  const columns = useMemo(() => {
    return [
      ...headers
        .filter((header) => !hiddenColumns.includes(header.idCanvas))
        .map((header) => ({
          Header: header.label,
          accessor: header.idCanvas,
        })),
    ]
  }, [headers, hiddenColumns])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    gotoPage,
    selectedFlatRows,
    state: { pageIndex, sortBy }, // Directly destructure pageIndex and pageSize
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      manualPagination: true,
      pageCount,
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect
  )

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true)
        const sort = mySortBy
        let sortId = null
        let sortOrder = 'asc'

        if (sort) {
          sortId = sort.id
          sortOrder = sort.desc ? 'desc' : 'asc'
        }

        const response = await collectFormAnswersData(
          builderContext.url_code,
          currentPage,
          pageSize,
          false, // C'est le paramètre 'all_data'
          sortId,
          sortOrder,
          searchQuery,
          columnSearch
        )
        console.log('response', response)

        // Mettre à jour la source des données
        setData(response.results.rows)

        // Calcul du nombre de pages
        setPageCount(Math.ceil(response.count / pageSize))

        // Sauvegardez les en-têtes pour une utilisation ultérieure (nouvelle ligne)
        setHeaders(response.results.headers)
        setLoading(false)
      } catch (error) {
        console.error('Error fetching data: ', error)
        setLoading(false)
      }
    }
    fetchData()
  }, [
    builderContext.url_code,
    currentPage,
    pageSize,
    mySortBy,
    searchQuery,
    columnSearch,
  ])

  useEffect(() => {
    setSelectedRows(new Set(selectedFlatRows.map((row) => row.original)))
  }, [selectedFlatRows])

  // Lorsque la recherche globale change, réinitialisez également la page courante sur 1
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
    setCurrentPage(1) // Réinitialiser à la première page
    gotoPage(0) // gotoPage utilise un index basé sur 0 pour les pages
  }

  // Lorsque la recherche par colonne change, réinitialisez également la page courante sur 1
  const handleColumnSearchChange = (columnId, value) => {
    const newSearch = {
      ...columnSearch,
      [columnId]: value,
    }
    setColumnSearch(newSearch)
    setCurrentPage(1) // Réinitialiser à la première page
    gotoPage(0) // gotoPage utilise un index basé sur 0 pour les pages
  }

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage)
    gotoPage(newPage - 1) // Utilisez ceci à la place de setTableState
  }

  const handleSortBy = (columnId, desc = false) => {
    setMySortBy({ id: columnId, desc })
    console.log('columnId', columnId)
  }

  const handlePageSizeChange = (value) => {
    setPageSize(value)
    setCurrentPage(1)
  }

  const toggleHideColumn = (columnId) => {
    setHiddenColumns((prevHiddenColumns) =>
      prevHiddenColumns.includes(columnId)
        ? prevHiddenColumns.filter((id) => id !== columnId)
        : [...prevHiddenColumns, columnId]
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.topControls}>
        <Input
          placeholder="Recherche..."
          value={searchQuery}
          onChange={handleSearchChange} // Utilisez le nouveau gestionnaire ici
        />

        <Select
          defaultValue={10}
          style={{ width: 120 }}
          onChange={handlePageSizeChange}
        >
          <Option value={2}>2 / page</Option>
          <Option value={10}>10 / page</Option>
          <Option value={20}>20 / page</Option>
          <Option value={50}>50 / page</Option>
        </Select>

        <div className={styles.columnToggle}>
          {headers.map((header) => (
            <label key={header.idCanvas}>
              <input
                type="checkbox"
                checked={!hiddenColumns.includes(header.idCanvas)}
                onChange={() => toggleHideColumn(header.idCanvas)}
              />
              {header.label}
            </label>
          ))}
        </div>
      </div>
      <table {...getTableProps()} className={styles.table}>
        <thead>
          {headerGroups.map((headerGroup, index) => (
            <React.Fragment key={'headerGroup-' + index}>
              {index === 0 && (
                <tr key={'search-row'}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps()}>
                      <Input
                        placeholder={`Rechercher ${column.render('Header')}...`}
                        value={columnSearch[column.id] || ''}
                        onChange={(e) =>
                          handleColumnSearchChange(column.id, e.target.value)
                        } // Utilisez le nouveau gestionnaire ici
                      />
                    </th>
                  ))}
                </tr>
              )}
              <tr
                {...headerGroup.getHeaderGroupProps()}
                key={'header-' + index}
              >
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    onClick={() => handleSortBy(column.id, !mySortBy?.desc)}
                  >
                    {column.render('Header')}
                    <span>
                      {mySortBy?.id === column.accessor
                        ? mySortBy?.desc
                          ? ' 🔽'
                          : ' 🔼'
                        : ''}
                    </span>
                  </th>
                ))}
              </tr>
            </React.Fragment>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: highlightText(
                            (cell.value || '').toString(),
                            searchQuery
                          ),
                        }}
                      />
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className={styles.pagination}>
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || rows.length === 0}
        >
          {'<<'}
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || rows.length === 0}
        >
          {'<'}
        </button>
        <span>
          Page{' '}
          <strong>
            {currentPage} of {pageCount}
          </strong>
        </span>

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === pageCount || rows.length === 0}
        >
          {'>'}
        </button>
        <button
          onClick={() => handlePageChange(pageCount)}
          disabled={currentPage === pageCount || rows.length === 0}
        >
          {'>>'}
        </button>
      </div>
    </div>
  )
}

export default AnswersV2
